/** Shared choropleth legend types and helpers for map + layer settings. */

export interface ChoroplethColorStop {
  value: number;
  color: string;
}

export interface ChoroplethLegendSpec {
  title: string;
  unit: string;
  stops: readonly ChoroplethColorStop[];
}

export function buildInterpolateFillColor(stops: readonly ChoroplethColorStop[]): unknown[] {
  return [
    'interpolate',
    ['linear'],
    ['get', 'value'],
    ...stops.flatMap(({ value, color }) => [value, color]),
  ];
}

export function choroplethGradientCss(stops: readonly ChoroplethColorStop[]): string {
  if (stops.length === 0) return 'transparent';
  const min = stops[0].value;
  const max = stops[stops.length - 1].value;
  const span = max - min || 1;
  const segments = stops.map(({ value, color }) => {
    const pct = ((value - min) / span) * 100;
    return `${color} ${pct}%`;
  });
  return `linear-gradient(to right, ${segments.join(', ')})`;
}

export function stopPercent(value: number, stops: readonly ChoroplethColorStop[]): number {
  const min = stops[0].value;
  const max = stops[stops.length - 1].value;
  const span = max - min || 1;
  return ((value - min) / span) * 100;
}

export function formatLegendValue(value: number): string {
  if (Math.abs(value) >= 100) return String(Math.round(value));
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(1).replace(/\.0$/, '');
}

export function legendTitleWithUnit(title: string, unit: string): string {
  return unit ? `${title} [${unit}]` : title;
}

/** Align tick labels with bar ends — first/last flush, middle centred. */
export function stopLabelPosition(
  index: number,
  count: number,
  pct: number,
): { left: string; transform: string } {
  if (count <= 1) return { left: '0%', transform: 'none' };
  if (index === 0) return { left: '0%', transform: 'none' };
  if (index === count - 1) return { left: '100%', transform: 'translateX(-100%)' };
  return { left: `${pct}%`, transform: 'translateX(-50%)' };
}
